/**
 * Network Graph View - SNA Visualization for Token Examiner
 *
 * Displays relationship rollup data from the 3-pass agentic TE system
 * using D3.js force-directed graph. Designed for Social Network Analysis (SNA)
 * based on Stephen Borgatti's research principles.
 *
 * Data source: entities/{entityId}/relationship_rollups/*
 * (NOT discoveredRelationships - that's legacy pre-agentic system)
 */

import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';
import { Box, Paper, Typography, CircularProgress, Alert } from '@mui/material';
import api from '../../../services/api';

// eslint-disable-next-line max-lines-per-function
function NetworkGraphView({ entityId }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [graphData, setGraphData] = useState({ nodes: [], edges: [], metadata: {} });

  // Fetch network graph data
  useEffect(() => {
    const fetchGraphData = async () => {
      if (!entityId) return;

      try {
        setLoading(true);
        setError(null);

        const response = await api.getNetworkGraphData(entityId);

        if (response.success) {
          setGraphData({
            nodes: response.nodes || [],
            edges: response.edges || [],
            metadata: response.metadata || {}
          });
        } else {
          throw new Error('Failed to fetch network graph data');
        }
      } catch (err) {
        console.error('Error fetching network graph:', err);
        setError(err.message || 'Failed to load network graph');
      } finally {
        setLoading(false);
      }
    };

    fetchGraphData();
  }, [entityId]);

  // Update dimensions
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect();
        setDimensions({ width, height });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  // Render force-directed graph
  // eslint-disable-next-line max-lines-per-function
  useEffect(() => {
    if (loading || !dimensions.width || !dimensions.height || !graphData.nodes.length) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const { nodes, edges } = graphData;

    // Node color scheme by type
    const nodeColors = {
      person: '#2196F3',
      organization: '#4CAF50',
      program: '#FF9800',
      community: '#9C27B0',
      location: '#00BCD4',
      unknown: '#757575'
    };

    // Create main container with zoom
    const container = svg.append('g');

    // Add zoom behavior
    const zoom = d3.zoom()
      .scaleExtent([0.1, 4])
      .on('zoom', (event) => {
        container.attr('transform', event.transform);
      });

    svg.call(zoom);

    // Create force simulation (Borgatti spring embedding principles)
    const simulation = d3.forceSimulation(nodes)
      .force('link', d3.forceLink(edges)
        .id(d => d.id)
        .distance(d => 100 - (d.confidence * 50)) // Closer if higher confidence
        .strength(0.8)
      )
      .force('charge', d3.forceManyBody()
        .strength(-300)
        .distanceMax(400)
      )
      .force('center', d3.forceCenter(dimensions.width / 2, dimensions.height / 2))
      .force('collision', d3.forceCollide()
        .radius(d => 15 + Math.sqrt(d.mentionCount || 1) * 3)
        .strength(0.9)
      )
      .alphaDecay(0.01)
      .velocityDecay(0.3);

    // Create links (edges)
    const linkElements = container.selectAll('.link')
      .data(edges)
      .enter().append('line')
      .attr('class', 'link')
      .attr('stroke', '#999')
      .attr('stroke-width', d => Math.max(1, (d.confidence || 0) * 3))
      .attr('stroke-opacity', 0.6)
      .attr('marker-end', 'url(#arrowhead)');

    // Add arrowhead marker definition
    svg.append('defs').append('marker')
      .attr('id', 'arrowhead')
      .attr('viewBox', '-0 -5 10 10')
      .attr('refX', 20)
      .attr('refY', 0)
      .attr('orient', 'auto')
      .attr('markerWidth', 6)
      .attr('markerHeight', 6)
      .append('path')
      .attr('d', 'M 0,-5 L 10,0 L 0,5')
      .attr('fill', '#999')
      .attr('opacity', 0.6);

    // Create node groups
    const nodeElements = container.selectAll('.node')
      .data(nodes)
      .enter().append('g')
      .attr('class', 'node')
      .call(d3.drag()
        .on('start', (event, d) => {
          if (!event.active) simulation.alphaTarget(0.1).restart();
          d.fx = d.x;
          d.fy = d.y;
        })
        .on('drag', (event, d) => {
          d.fx = event.x;
          d.fy = event.y;
        })
        .on('end', (event, d) => {
          if (!event.active) simulation.alphaTarget(0);
          d.fx = null;
          d.fy = null;
        })
      );

    // Node circles
    nodeElements.append('circle')
      .attr('r', d => 10 + Math.sqrt(d.mentionCount || 1) * 3)
      .attr('fill', d => nodeColors[d.type?.toLowerCase()] || nodeColors.unknown)
      .attr('stroke', '#fff')
      .attr('stroke-width', 2)
      .style('cursor', 'pointer')
      .on('mouseenter', function(event, d) {
        // Enlarge node
        d3.select(this)
          .transition()
          .duration(200)
          .attr('r', (10 + Math.sqrt(d.mentionCount || 1) * 3) * 1.3);

        // Highlight connected links
        linkElements
          .attr('stroke-opacity', link =>
            (link.source.id === d.id || link.target.id === d.id) ? 1 : 0.1
          );

        // Show tooltip with node details
        const tooltip = d3.select('body').append('div')
          .attr('class', 'network-tooltip')
          .style('position', 'absolute')
          .style('background', 'rgba(0,0,0,0.8)')
          .style('color', '#fff')
          .style('padding', '8px')
          .style('border-radius', '4px')
          .style('font-size', '12px')
          .style('pointer-events', 'none')
          .style('z-index', 10000)
          .html(`
            <strong>${d.label}</strong><br/>
            Type: ${d.type}<br/>
            Mentions: ${d.mentionCount}<br/>
            Documents: ${d.documentCount}
          `)
          .style('left', (event.pageX + 10) + 'px')
          .style('top', (event.pageY - 10) + 'px');

        d3.select(this).on('mousemove', (e) => {
          tooltip
            .style('left', (e.pageX + 10) + 'px')
            .style('top', (e.pageY - 10) + 'px');
        });
      })
      .on('mouseleave', function(event, d) {
        // Reset node size
        d3.select(this)
          .transition()
          .duration(200)
          .attr('r', 10 + Math.sqrt(d.mentionCount || 1) * 3);

        // Reset link opacity
        linkElements.attr('stroke-opacity', 0.6);

        // Remove tooltip
        d3.selectAll('.network-tooltip').remove();
      })
      .on('click', (event, d) => {
        console.log('Node clicked:', d);
        // TODO: Open detail panel showing all TE data for this node
      });

    // Node labels
    nodeElements.append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', 4)
      .style('font-size', '10px')
      .style('font-weight', 'bold')
      .style('fill', '#fff')
      .style('pointer-events', 'none')
      .text(d => {
        // Show first letters of label (token hash abbreviated)
        const label = d.label || d.id;
        return label.substring(0, 6);
      });

    // Update positions on simulation tick
    simulation.on('tick', () => {
      // Constrain nodes to bounds
      nodes.forEach(d => {
        const radius = 10 + Math.sqrt(d.mentionCount || 1) * 3;
        d.x = Math.max(radius, Math.min(dimensions.width - radius, d.x));
        d.y = Math.max(radius, Math.min(dimensions.height - radius, d.y));
      });

      linkElements
        .attr('x1', d => d.source.x)
        .attr('y1', d => d.source.y)
        .attr('x2', d => d.target.x)
        .attr('y2', d => d.target.y);

      nodeElements
        .attr('transform', d => `translate(${d.x},${d.y})`);
    });

    // Cleanup
    return () => {
      simulation.stop();
      d3.selectAll('.network-tooltip').remove();
    };

  }, [dimensions, graphData, loading]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!graphData.nodes.length) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info">
          No network data available. The Token Examiner 3-pass analysis may not have run yet,
          or no relationships were discovered in the processed documents.
        </Alert>
      </Box>
    );
  }

  return (
    <Box ref={containerRef} sx={{ width: '100%', height: '100%', position: 'relative' }}>
      <svg ref={svgRef} style={{ width: '100%', height: '100%' }} />

      {/* Legend */}
      <Paper
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          p: 2,
          maxWidth: 250,
          backgroundColor: 'rgba(255,255,255,0.95)'
        }}
        elevation={3}
      >
        <Typography variant="subtitle2" gutterBottom>
          Network Statistics
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" display="block">
            Nodes: {graphData.nodes.length}
          </Typography>
          <Typography variant="caption" display="block">
            Edges: {graphData.edges.length}
          </Typography>
          <Typography variant="caption" display="block">
            Documents: {graphData.metadata.documentCount || 0}
          </Typography>
          <Typography variant="caption" display="block">
            Analyses: {graphData.metadata.analysisCount || 0}
          </Typography>
        </Box>

        <Typography variant="subtitle2" gutterBottom>
          Node Types
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {[
            { label: 'Person', color: '#2196F3' },
            { label: 'Organization', color: '#4CAF50' },
            { label: 'Program', color: '#FF9800' },
            { label: 'Community', color: '#9C27B0' },
            { label: 'Location', color: '#00BCD4' },
          ].map(item => (
            <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: item.color,
                  border: '1px solid #fff'
                }}
              />
              <Typography variant="caption">{item.label}</Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #eee' }}>
          <Typography variant="caption" display="block" color="text.secondary">
            💡 Drag nodes to reposition
          </Typography>
          <Typography variant="caption" display="block" color="text.secondary">
            🔍 Zoom with scroll wheel
          </Typography>
          <Typography variant="caption" display="block" color="text.secondary">
            📍 Click nodes for details (coming soon)
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
}

export default NetworkGraphView;
